// src/hooks/useScrollAnimation.js
import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook for scroll-based animations using Intersection Observer
 * @param {number} threshold - The percentage of the element that needs to be visible (0-1)
 * @param {object} options - Additional Intersection Observer options
 * @returns {array} - [ref, isVisible] - ref to attach to element and visibility state
 */
export const useScrollAnimation = (threshold = 0.1, options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Only trigger animation once for performance
        if (entry.isIntersecting && !hasAnimated) {
          setIsVisible(true);
          setHasAnimated(true);
        }
      },
      {
        threshold,
        rootMargin: '0px 0px -50px 0px', // Trigger slightly before element comes into view
        ...options
      }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold, hasAnimated, options]);

  return [ref, isVisible];
};

/**
 * Hook for staggered animations of multiple elements
 * @param {number} itemCount - Number of items to animate
 * @param {number} delay - Delay between each item animation (in ms)
 * @param {number} threshold - Intersection threshold
 * @returns {array} - [ref, visibleItems] - ref for container and array of visible states
 */
export const useStaggeredAnimation = (itemCount, delay = 100, threshold = 0.1) => {
  const [visibleItems, setVisibleItems] = useState(new Array(itemCount).fill(false));
  const [containerVisible, setContainerVisible] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !containerVisible) {
          setContainerVisible(true);
          
          // Stagger the animation of each item
          for (let i = 0; i < itemCount; i++) {
            setTimeout(() => {
              setVisibleItems(prev => {
                const newState = [...prev];
                newState[i] = true;
                return newState;
              });
            }, i * delay);
          }
        }
      },
      { threshold }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [itemCount, delay, threshold, containerVisible]);

  return [ref, visibleItems];
};

/**
 * Hook for scroll progress tracking
 * @returns {number} - Scroll progress from 0 to 1
 */
export const useScrollProgress = () => {
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = window.scrollY / totalHeight;
      setScrollProgress(Math.min(Math.max(progress, 0), 1));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return scrollProgress;
};

/**
 * Hook for parallax scroll effects
 * @param {number} speed - Parallax speed multiplier (0-1)
 * @returns {object} - Transform style object
 */
export const useParallax = (speed = 0.5) => {
  const [transform, setTransform] = useState('translateY(0px)');
  const ref = useRef();

  useEffect(() => {
    const handleScroll = () => {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        const scrolled = window.scrollY;
        const rate = scrolled * -speed;
        setTransform(`translateY(${rate}px)`);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [speed]);

  return [ref, { transform }];
};

export default useScrollAnimation;